import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, DoBootstrap, Injector, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { createCustomElement } from '@angular/elements';

// Modules
import { BrowserModule } from '@angular/platform-browser';
import { CoreModule } from '@core/core.module';
import {
  FeatureToggleModule,
  SecurityModule,
  SecurityService,
} from '@mynexus/mynexus-ui-lib';

// Services
import { SDoHMfeService } from './core/service/sdoh-mfe.service';

// Components
import { MncSDoHComponent } from './mnc-sdoh.component';
import { EmptyComponent } from '@shared/empty/empty.component';
import { PipeModule } from './shared/pipe/pipe.module';
import { environment } from '@env/environment';
import { PrimeNGConfigLoader, providePrimeNGFromShell, RouteInterceptor, ShellService, TokenInterceptor } from '@mynexus/mfe-core';
import { HttpErrorHandler, HttpUtility } from './core/http';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { MncSDoHRoutingModule } from './mnc-sdoh-routing.module';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';

function loadConfig(
  shellService: ShellService,
  securityService: SecurityService) {
  return async () => {
    try {
      // Configure permission module
      securityService.setUserPermissions(shellService.authenticationService.userPermissions);

      // Legacy permissions model force us to set this false to avoid problem with empty permissions users
      securityService.setAllowEmptyPermission(false);
    } catch (e: any) {
      console.log(`Error load config data: ${e.message}`);
    }
  };
}

/**
 * MFE-specific default theme loader. Lazy-loads `@mynexus/theme` so the Carelon
 * preset is only fetched when the shell hasn't supplied its own theme.
 */
const loadCarelonPrimeNGConfig: PrimeNGConfigLoader = async () => {
  const { Carelon, CSS_LAYERS_ORDER } = await import('@mynexus/theme');

  return {
    ripple: false,
    theme: {
      preset: Carelon,
      options: {
        darkModeSelector: false,
        cssLayer: {
          name: 'primeng',
          order: CSS_LAYERS_ORDER
        }
      },
    }
  };
};

@NgModule({
  declarations: [
    MncSDoHComponent,
    EmptyComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CoreModule,
    FeatureToggleModule,
    ConfirmDialogModule,
    // Uncomment if the application needs routing.
    MncSDoHRoutingModule,
    ToastModule,
    PipeModule,
    FeatureToggleModule.forRoot(`${environment.apiUrl + '/api/'}`),
    SecurityModule.forRoot([], environment.apiUrl),
  ],
  exports: [
    ...environment.modules,
  ],
  providers: [
    providePrimeNGFromShell(loadCarelonPrimeNGConfig),
    HttpUtility,
    HttpErrorHandler,
    MessageService,
    ConfirmationService,
    DialogService,
    {provide: 'windowRef', useValue: window},
    provideAppInitializer(() => {
      const initializerFn = (loadConfig)(inject(ShellService), inject(SecurityService));
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RouteInterceptor,
      multi: true
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MncSDoHModule implements DoBootstrap {
  constructor(
    private injector: Injector,
    shellService: ShellService,
    mfeService: SDoHMfeService
  ) {
    this.registerCustomElement('mnc-sdoh', MncSDoHComponent);

    shellService.registerMfeService('mnc-sdoh', mfeService);
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  ngDoBootstrap(): void {
  }

  private registerCustomElement(name: string, component: any) {
    if (customElements.get(name))
      return;

    customElements.define(
      name,
      createCustomElement<any>(component, {injector: this.injector}));
  }
}
